import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import T from 'components/common/text/T';
import { ISystemSetting } from 'lib/types/userTypes';
import CustomSpan from 'components/common/text/CustomSpan';
import { IconButton } from '@material-ui/core';
import { icon_back_large } from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import { color } from 'styles/utils';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import BasicTooltip from 'components/common/tooltip/BasicTooltip';
import cx from 'classnames';
import { regNumberOfString } from 'lib/library';
import ClinicModal from 'components/common/modal/ClinicModal';
import { useTranslation } from 'react-i18next';
import { IScanIOSConfigResponse, TConfigValue, TScanPathType } from 'lib/types/utilTypes';

interface ScanIosSettingContainerProps {
  data: ISystemSetting | null;
  onClickGoBack: () => void;
}

type TScanIOSMsg = 'SYSTEM_SAVE_SUCCESS' | 'SYSTEM_DISK_NOT_ENOUGH';

function ScanIosSettingContainer({ data, onClickGoBack }: ScanIosSettingContainerProps) {
  const RECOVERY_PATH = 'SCAN_IOS_RECOVERY_PATH';
  const BACKUP_PATH = 'SCAN_IOS_BACKUP_PATH';
  const SYSTEM_SAVE_SUCCESS = 'SYSTEM_SAVE_SUCCESS';
  const SYSTEM_DISK_NOT_ENOUGH = 'SYSTEM_DISK_NOT_ENOUGH';
  const { ipcRenderer } = window;

  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [recoveryConfig, setRecoveryConfig] = useState<TConfigValue>({ path: '', volume: '' });
  const [backupConfig, setBackupConfig] = useState<TConfigValue>({ path: '', volume: '' });
  const backupInputRef = useRef<HTMLInputElement>(null);
  const recoveryInputRef = useRef<HTMLInputElement>(null);
  const [backupFocus, setBackupFocus] = useState<boolean>(false);
  const [recoveryFocus, setRecoveryFocus] = useState<boolean>(false);
  const [backupVolumeError, setBackupVolumeError] = useState<boolean>(false);
  const [recoveryVolumeError, setRecoveryVolumeError] = useState<boolean>(false);
  const [scanIOSConfigRes, setScanIOSConfigRes] = useState<IScanIOSConfigResponse | null>(null);
  const [isDiabledSave, setIsDiabledSave] = useState<boolean>(true);

  const [alertModalTextCode, setAlertModalTextCode] = useState<TScanIOSMsg>(SYSTEM_SAVE_SUCCESS);

  const handleChangeVolume = useCallback(
    (e: ChangeEvent<HTMLInputElement>, type: TScanPathType) => {
      if (!regNumberOfString(e.target.value) || e.target.value.length > 4) {
        return;
      } else {
        if (type === BACKUP_PATH) {
          setBackupConfig({ ...backupConfig, volume: e.target.value });
        }
        if (type === RECOVERY_PATH) {
          setRecoveryConfig({ ...recoveryConfig, volume: e.target.value });
        }
        if (!!backupConfig.path && !!recoveryConfig.path) {
          setIsDiabledSave(false);
        }
      }
    },
    [backupConfig, recoveryConfig],
  );

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setIsDiabledSave(true);
    if (!scanIOSConfigRes) {
      setBackupVolumeError(false);
      setRecoveryVolumeError(false);
    } else {
      const backupStatus = scanIOSConfigRes.scanIOSBackup.status;
      const recoveryStatus = scanIOSConfigRes.scanIOSRecovery.status;
      if (!!backupStatus && !recoveryStatus) {
        setTimeout(() => {
          recoveryInputRef.current?.focus();
        }, 200);
      } else {
        setTimeout(() => {
          backupInputRef.current?.focus();
        }, 200);
      }

      if (!backupStatus) {
        setBackupVolumeError(true);
      }
      if (!recoveryStatus) {
        setRecoveryVolumeError(true);
      }
    }
  }, [scanIOSConfigRes]);

  const handleOpenDirectory = (directoryType: TScanPathType) => {
    if (!ipcRenderer) return;
    ipcRenderer.send('openDirectory', { directoryType });
  };

  const handleGetScanIosConfig = async () => {
    const res = await ipcRenderer.invoke('getScanIOSConfig', {});
    const backup = res.scanIOSBackup;
    const recovery = res.scanIOSRecovery;
    setRecoveryConfig({ path: recovery.path, volume: `${recovery.volume}` });
    setBackupConfig({ path: backup.path, volume: `${backup.volume}` });
    if (!!recovery.path) {
      backupInputRef.current?.focus();
    }
  };

  const handleSubmitScanIosConfig = useCallback(async () => {
    const submitData = {
      scanIOSRecovery: {
        ...recoveryConfig,
      },
      scanIOSBackup: {
        ...backupConfig,
      },
    };
    const res = await ipcRenderer.invoke('setScanIOSConfig', submitData);
    if (!res.status) {
      setScanIOSConfigRes(res);
      setAlertModalTextCode(SYSTEM_DISK_NOT_ENOUGH);
      setOpenModal(true);
    } else {
      setScanIOSConfigRes(null);
      setAlertModalTextCode(SYSTEM_SAVE_SUCCESS);
      setOpenModal(true);
    }
  }, [backupConfig, recoveryConfig]);

  useEffect(() => {
    if (!scanIOSConfigRes) return;
    const resBackup = scanIOSConfigRes.scanIOSBackup;
    const resRecovery = scanIOSConfigRes.scanIOSRecovery;
    if (
      resBackup.volume === backupConfig.volume &&
      resBackup.path === backupConfig.path &&
      resRecovery.volume === recoveryConfig.volume &&
      resRecovery.path === recoveryConfig.path
    ) {
      setBackupVolumeError(true);
      setRecoveryVolumeError(true);
      setIsDiabledSave(true);
    } else {
      setBackupVolumeError(false);
      setRecoveryVolumeError(false);
      setIsDiabledSave(false);
    }
  }, [scanIOSConfigRes, backupConfig, recoveryConfig]);

  useEffect(() => {
    if (!ipcRenderer) return;
    handleGetScanIosConfig();
    return () => {
      setRecoveryConfig({ path: '', volume: '' });
      setBackupConfig({ path: '', volume: '' });
    };
  }, []);

  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.on(
      'openDirectoryRes',
      (e: any, data: { directoryType: string; canceled: boolean; filePaths: string[] }) => {
        if (!!data.canceled) return;
        if (data.directoryType === BACKUP_PATH) {
          setBackupConfig({ ...backupConfig, path: data.filePaths[0] });
        }
        if (data.directoryType === RECOVERY_PATH) {
          setRecoveryConfig({ ...recoveryConfig, path: data.filePaths[0] });
        }
      },
    );
  }, [recoveryConfig, backupConfig]);

  return (
    <StyledScanIosSetting data-component-name="ScanIosSetting">
      <div className="scanIosSetting__title_box">
        <IconButton className="scanIosSetting__goback_icon" onClick={onClickGoBack}>
          <img src={icon_back_large} />
        </IconButton>
        <CustomSpan fontSize={24} fontWeight={600}>
          <T>{data?.textCode}</T>
        </CustomSpan>
      </div>
      <div className="scanIosSetting__container">
        {/* Backup */}
        <div className="scanIosSetting__contents_box">
          <div className="scanIosSetting__contents_title">
            <CustomSpan fontSize={18} fontWeight={600}>
              <T>SYSTEM_BACKUP_SETTING</T>
            </CustomSpan>
          </div>
          <div className="scanIosSetting__contents_row">
            <div className="row_item_table">
              <div className="item_label">
                <CustomSpan fontSize={14} fontWeight={500} fontColor="#00A4E3">
                  <T>SYSTEM_BACKUP_PATH</T>
                </CustomSpan>
              </div>
              <BasicTooltip
                className="item_contents"
                text={backupConfig.path}
                placement="bottom-start"
              >
                <div className="item_contents_text">
                  <CustomSpan fontSize={14} fontWeight={400} fontColor="#071138">
                    {backupConfig.path}
                  </CustomSpan>
                </div>
              </BasicTooltip>
            </div>
            <IconButton
              className="row_item_icon"
              onClick={() => {
                handleOpenDirectory(BACKUP_PATH);
              }}
            >
              <MoreHorizIcon />
            </IconButton>
          </div>
          <div className="scanIosSetting__contents_row">
            <div className="row_item_table">
              <div className="item_label">
                <CustomSpan fontSize={14} fontWeight={500} fontColor="#00A4E3">
                  <T>SYSTEM_VOLUME_LIMIT</T>
                </CustomSpan>
              </div>
              <input
                className={cx('item_contents', {
                  backupFocus,
                  error: backupVolumeError,
                })}
                disabled={!backupConfig.path}
                ref={backupInputRef}
                value={backupConfig.volume}
                onChange={e => {
                  handleChangeVolume(e, BACKUP_PATH);
                }}
                onFocus={() => {
                  setBackupFocus(true);
                }}
                onBlur={() => {
                  setBackupFocus(false);
                }}
              />
            </div>
            <CustomSpan fontSize={16} fontWeight={600} fontColor="#444">
              GB
            </CustomSpan>
          </div>
        </div>
        {/* Recovery */}
        <div className="scanIosSetting__contents_box">
          <div className="scanIosSetting__contents_title">
            <CustomSpan fontSize={18} fontWeight={600}>
              <T>SYSTEM_RECOVERY_SETTING</T>
            </CustomSpan>
          </div>
          <div className="scanIosSetting__contents_row">
            <div className="row_item_table">
              <div className="item_label">
                <CustomSpan fontSize={14} fontWeight={500} fontColor="#00A4E3">
                  <T>SYSTEM_RECOVERY_PATH</T>
                </CustomSpan>
              </div>
              <BasicTooltip
                className="item_contents"
                text={recoveryConfig.path}
                placement="bottom-start"
              >
                <div className="item_contents_text">
                  <CustomSpan fontSize={14} fontWeight={400} fontColor="#071138">
                    {recoveryConfig.path}
                  </CustomSpan>
                </div>
              </BasicTooltip>
            </div>
            <IconButton
              className="row_item_icon"
              onClick={() => {
                handleOpenDirectory(RECOVERY_PATH);
              }}
            >
              <MoreHorizIcon />
            </IconButton>
          </div>
          <div className="scanIosSetting__contents_row">
            <div className="row_item_table">
              <div className="item_label">
                <CustomSpan fontSize={14} fontWeight={500} fontColor="#00A4E3">
                  <T>SYSTEM_VOLUME_LIMIT</T>
                </CustomSpan>
              </div>
              <input
                className={cx('item_contents', {
                  recoveryFocus,
                  error: recoveryVolumeError,
                })}
                disabled={!recoveryConfig.path}
                ref={recoveryInputRef}
                value={recoveryConfig.volume}
                onChange={e => {
                  handleChangeVolume(e, RECOVERY_PATH);
                }}
                onFocus={() => {
                  setRecoveryFocus(true);
                }}
                onBlur={() => {
                  setRecoveryFocus(false);
                }}
              />
            </div>
            <CustomSpan fontSize={16} fontWeight={600} fontColor="#444">
              GB
            </CustomSpan>
          </div>
        </div>
      </div>
      <StyledPlainButtonOuter backgroundColor={color.navy_blue} left="50%" width={370} height={76}>
        <MuiButton
          type="submit"
          config={{
            width: '320px',
          }}
          disableElevation
          color="primary"
          variant="contained"
          disabled={isDiabledSave}
          disablebackground="#17288a"
          disablefontcolor="#ffffff"
          className="xl border-radius-round RemakeTypeEditModal__modify_btn"
          onClick={() => {
            handleSubmitScanIosConfig();
          }}
        >
          <CustomSpan fontSize={16}>
            <T>GLOBAL_SAVE</T>
          </CustomSpan>
        </MuiButton>
      </StyledPlainButtonOuter>

      {useMemo(() => {
        return (
          <ClinicModal
            title={t('CLINIC_MODAL_TITLE_NOTICE')}
            isOpen={openModal}
            onClose={handleCloseModal}
            onConfirm={handleCloseModal}
          >
            <SettingAlertModal textCode={alertModalTextCode} />
          </ClinicModal>
        );
      }, [openModal, alertModalTextCode])}
    </StyledScanIosSetting>
  );
}
const StyledScanIosSetting = styled.div<{}>`
  display: flex;
  flex-direction: column;
  row-gap: 48px;
  .scanIosSetting__title_box {
    display: flex;
    align-items: center;
    column-gap: 10px;
    .scanIosSetting__goback_icon {
      width: 34px;
      height: 34px;
      padding: 3px;
      img {
        width: 28px;
        height: 28px;
      }
    }
  }

  .scanIosSetting__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 40px;
    width: 100%;
    .scanIosSetting__contents_box {
      width: 540px;
      display: flex;
      flex-direction: column;
      row-gap: 10px;
      .scanIosSetting__contents_row {
        width: 100%;
        display: flex;
        align-items: center;
        column-gap: 10px;
        .row_item_table {
          display: flex;
          .item_label {
            width: 140px;
            background-color: #edf4fb;
            border-radius: 3px 0 0 3px;
            border-top: 1px solid #e1e1e7;
            border-left: 1px solid #e1e1e7;
            border-bottom: 1px solid #e1e1e7;
            border-right: none;
            justify-content: center;
            display: flex;
            align-items: center;
            height: 40px;
          }
          .item_contents {
            width: 350px;
            border: 1px solid #e1e1e7;
            border-radius: 0 3px 3px 0;
            display: flex;
            align-items: center;
            height: 40px;
            padding: 0 10px;
            .item_contents_text {
              display: flex;
              align-items: center;
              width: 350px;
              height: 40px;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            &.backupFocus {
              border: 1px solid #00a6e2;
            }
            &.error {
              border: 1px solid red;
            }
          }
        }
        .row_item_icon {
          width: 40px;
          height: 40px;
          border: 1px solid #e1e1e7;
          border-radius: 3px;
        }
      }
    }
  }
  .AsyncFetchSelect__input {
    width: 100%;
    height: 38px;
    /* padding: 0 8px; */
    /* padding-right: 55px; */

    background-color: #ffffff;
    /* border: 1px solid rgba(0, 0, 0, 0.23); */
    border: none;
    /* border-color: #e1e1e1; */
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    &:hover {
      border-color: #7cbef8;
    }
    &:focus {
      border-color: ${color.blue};
    }
    &::placeholder {
      color: #e1e1e1;
    }
  }
`;

interface SettingAlertModalProps {
  textCode: string;
}

function SettingAlertModal({ textCode }: SettingAlertModalProps) {
  return (
    <StyledSettingAlertModal data-component-name="SettingAlertModal">
      <div className="settingAlertModal__description">
        <CustomSpan>
          <T>{textCode}</T>
        </CustomSpan>
      </div>
    </StyledSettingAlertModal>
  );
}

const StyledSettingAlertModal = styled.div`
  .settingAlertModal__description {
    padding: 20px 0;
  }
`;
export default React.memo(ScanIosSettingContainer);
